import * as React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  LinearProgress,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { withTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import HelpButton from '../../shared/HelpButton';
import { useWorkOrdersReport } from '../hooks/useReportsApi';

const STATUS_COLORS = {
  planned: '#64748b',
  in_progress: '#3b82f6',
  completed: '#10b981',
  awaiting_pickup: '#f59e0b',
};

function WorkOrdersTab({ t, dateParams }) {
  const { data, loading, error } = useWorkOrdersReport(dateParams);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      {/* Export Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="work-orders" params={dateParams} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={BuildIcon}
            label={t('reports.workOrders.total')}
            value={data?.total}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={CheckCircleIcon}
            label={t('reports.workOrders.completed')}
            value={data?.completed}
            loading={loading}
            iconColor="success.main"
            iconBgColor="rgba(16, 185, 129, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={HourglassEmptyIcon}
            label={t('reports.workOrders.inProgress')}
            value={data?.in_progress}
            loading={loading}
            iconColor="info.main"
            iconBgColor="rgba(59, 130, 246, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={AccessTimeIcon}
            label={t('reports.workOrders.avgTurnaround')}
            value={data?.avg_turnaround_days}
            loading={loading}
            format="decimal"
          />
        </Grid>
      </Grid>

      {/* Completion Rate */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('reports.workOrders.completionRate')}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
            {loading ? '-' : `${(data?.completion_rate || 0).toFixed(1)} %`}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton variant="rectangular" height={10} />
        ) : (
          <LinearProgress
            variant="determinate"
            value={data?.completion_rate || 0}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'success.main',
                borderRadius: 5,
              },
            }}
          />
        )}
      </Paper>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Monthly Trend */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.workOrders.monthlyTrend')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data?.monthly_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="created" name={t('reports.workOrders.created')} fill="#3b82f6" />
                  <Bar dataKey="completed" name={t('reports.workOrders.completed')} fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Current Status Pie */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.workOrders.currentStatus')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (data?.current_status || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.current_status}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="status_display"
                  >
                    {data.current_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value, entry) => `${value} (${entry.payload?.count ?? 0})`}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('reports.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Turnaround Analysis */}
      {data?.turnaround_analysis && (
        <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('reports.workOrders.turnaroundAnalysis')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {data.turnaround_analysis.under_1_day.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('reports.workOrders.under1Day')} ({data.turnaround_analysis.under_1_day.percent}%)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {data.turnaround_analysis.one_to_3_days.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('reports.workOrders.1to3Days')} ({data.turnaround_analysis.one_to_3_days.percent}%)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.50', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {data.turnaround_analysis.over_3_days.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('reports.workOrders.over3Days')} ({data.turnaround_analysis.over_3_days.percent}%)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* By Job Type Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.workOrders.byJobType')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.workOrders.jobType')}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {t('reports.workOrders.count')}
                      <HelpButton text={t('reports.workOrders.countHelp')} size={16} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {t('reports.workOrders.revenue')}
                      <HelpButton text={t('reports.workOrders.revenueHelp')} size={16} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.by_job_type || []).map((job, index) => (
                  <TableRow key={index}>
                    <TableCell>{job.job_name}</TableCell>
                    <TableCell align="right">{job.count}</TableCell>
                    <TableCell align="right">
                      {job.revenue ? new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(job.revenue) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {(!data?.by_job_type || data.by_job_type.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(WorkOrdersTab);
